import React, { useState, useEffect, useCallback, useContext } from 'react';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ShieldCheck, RefreshCw, Lock, ChevronDown, ChevronRight } from 'lucide-react';

const ACTION_LABELS = {
  role_change: 'Alteração de role',
  warehouse_scope_change: 'Alteração de âmbito',
  permission_override_change: 'Override de permissões',
  view_all_toggle: 'Toggle ver-todos',
};

const ACTION_COLORS = {
  role_change: 'bg-red-100 text-red-700 border-red-200',
  warehouse_scope_change: 'bg-amber-100 text-amber-700 border-amber-200',
  permission_override_change: 'bg-blue-100 text-blue-700 border-blue-200',
  view_all_toggle: 'bg-purple-100 text-purple-700 border-purple-200',
};

function DiffBlock({ before, after }) {
  if (!before && !after) return null;
  return (
    <div className="grid md:grid-cols-2 gap-2 text-xs mt-1">
      <div className="bg-red-50 border border-red-100 rounded p-2">
        <div className="text-[10px] uppercase tracking-wide text-red-700 mb-1">Antes</div>
        <pre className="whitespace-pre-wrap break-all text-slate-700">{JSON.stringify(before, null, 2)}</pre>
      </div>
      <div className="bg-emerald-50 border border-emerald-100 rounded p-2">
        <div className="text-[10px] uppercase tracking-wide text-emerald-700 mb-1">Depois</div>
        <pre className="whitespace-pre-wrap break-all text-slate-700">{JSON.stringify(after, null, 2)}</pre>
      </div>
    </div>
  );
}

export default function AuditLog() {
  const { hasPermission } = useContext(AuthContext);
  const canView = hasPermission('audit.view');
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterAction, setFilterAction] = useState('all');
  const [filterEmail, setFilterEmail] = useState('');
  const [expanded, setExpanded] = useState(new Set());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: '200' });
      if (filterAction !== 'all') params.append('action', filterAction);
      const r = await axios.get(`${API}/admin/audit-log?${params.toString()}`);
      setEntries(r.data.entries || []);
    } catch (e) {
      toast.error('Erro ao carregar audit log');
    } finally { setLoading(false); }
  }, [filterAction]);

  useEffect(() => { if (canView) load(); else setLoading(false); }, [load, canView]);

  if (!canView) {
    return (
      <div className="p-8 max-w-xl mx-auto">
        <Card>
          <CardHeader className="flex flex-row items-center gap-3">
            <Lock className="h-5 w-5 text-amber-600" /><CardTitle>Acesso restrito</CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-muted-foreground">
            Precisas da permissão <code>audit.view</code>.
          </CardContent>
        </Card>
      </div>
    );
  }

  const filtered = filterEmail
    ? entries.filter((e) => (e.target_email || '').toLowerCase().includes(filterEmail.toLowerCase())
                          || (e.actor_email || '').toLowerCase().includes(filterEmail.toLowerCase()))
    : entries;

  const toggleExpand = (id) => {
    const n = new Set(expanded);
    if (n.has(id)) n.delete(id); else n.add(id);
    setExpanded(n);
  };

  return (
    <div className="min-h-screen bg-slate-50 pb-8" data-testid="audit-log-page">
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-[1200px] mx-auto px-4 md:px-6 py-4 md:py-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-3">
              <ShieldCheck className="h-5 w-5 md:h-6 md:w-6 text-red-600" />
              <div>
                <h1 className="text-xl md:text-2xl font-bold text-slate-900">Audit Log</h1>
                <p className="text-sm text-slate-500">Histórico de alterações críticas (roles, âmbitos, overrides)</p>
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="refresh-audit-btn" className="shrink-0">
              <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Atualizar
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-[1200px] mx-auto px-4 md:px-6 py-4 md:py-6 space-y-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <Select value={filterAction} onValueChange={setFilterAction}>
            <SelectTrigger className="w-full sm:w-64" data-testid="filter-action"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas as acções</SelectItem>
              {Object.entries(ACTION_LABELS).map(([k, v]) => (<SelectItem key={k} value={k}>{v}</SelectItem>))}
            </SelectContent>
          </Select>
          <Input placeholder="Procurar por email…" value={filterEmail} onChange={(e) => setFilterEmail(e.target.value)} className="flex-1 sm:max-w-sm" data-testid="filter-email" />
        </div>

        <Card>
          <CardContent className="p-0">
            {filtered.length === 0 ? (
              <div className="p-8 text-sm text-muted-foreground text-center">Nenhum evento registado.</div>
            ) : (
              <div className="divide-y" data-testid="audit-entries">
                {filtered.map((e) => (
                  <div key={e.id} className="px-4 py-3 hover:bg-slate-50/40">
                    <div className="flex items-start gap-3">
                      <button onClick={() => toggleExpand(e.id)} className="mt-1" data-testid={`expand-${e.id}`}>
                        {expanded.has(e.id) ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                      </button>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge className={`${ACTION_COLORS[e.action] || 'bg-slate-100'} text-xs`}>
                            {ACTION_LABELS[e.action] || e.action}
                          </Badge>
                          <span className="text-xs text-muted-foreground font-mono">{new Date(e.ts).toLocaleString('pt-PT')}</span>
                        </div>
                        <div className="text-sm mt-1">
                          <span className="font-medium">{e.actor_email || 'sistema'}</span>
                          <span className="text-muted-foreground"> alterou </span>
                          <span className="font-medium">{e.target_email || '(self)'}</span>
                        </div>
                        {expanded.has(e.id) && <DiffBlock before={e.before} after={e.after} />}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
